import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { isAuthenticated } from '../utils/auth';
import { DollarSign, Dog, Cat, PawPrint, CheckCircle } from 'lucide-react';

const NQ_CARE_CLINIC = 'NQ Care';

const PriceGuide = () => {
  const navigate = useNavigate();
  const clinic = JSON.parse(localStorage.getItem('vet_portal_clinic') || '{}');
  const isNQCare = clinic.clinic_name === NQ_CARE_CLINIC;

  useEffect(() => {
    if (!isAuthenticated()) { navigate('/login'); return; }
  }, [navigate]);

  const dogPricing = [
    { range: '1 – 10 kg', price: 450 },
    { range: '11 – 20 kg', price: 500 },
    { range: '21 – 30 kg', price: 550 },
    { range: '31 – 40 kg', price: 600 },
    { range: '41 – 50 kg', price: 650 },
    { range: '51 – 60 kg', price: 700 },
    { range: '61 – 70 kg', price: 800 },
    { range: '70+ kg', price: 890 },
  ];

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-8" data-testid="price-guide-page">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="text-4xl font-bold mb-2 text-primary" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Service Price Guide
          </h1>
          <p className="text-muted-foreground text-lg">Aquacremation pricing by pet type and weight</p>
        </motion.div>

        {/* Cat & Exotic Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="border-0 shadow-md hover:shadow-lg transition-all duration-300 h-full">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 rounded-xl bg-primary/10">
                    <Cat className="h-7 w-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>Cat</h3>
                    <p className="text-sm text-muted-foreground">Any weight</p>
                  </div>
                </div>
                <div className="text-center py-4 bg-primary/5 rounded-xl">
                  <p className="text-4xl font-bold text-primary" style={{ fontFamily: 'Fredoka, sans-serif' }}>$450</p>
                  <p className="text-sm text-muted-foreground mt-1">Flat rate</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card className="border-0 shadow-md hover:shadow-lg transition-all duration-300 h-full">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 rounded-xl bg-amber-500/10">
                    <PawPrint className="h-7 w-7 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>Exotic Pet</h3>
                    <p className="text-sm text-muted-foreground">Up to 1.9 kg</p>
                  </div>
                </div>
                <div className="text-center py-4 bg-amber-50 rounded-xl">
                  <p className="text-4xl font-bold text-amber-600" style={{ fontFamily: 'Fredoka, sans-serif' }}>$250</p>
                  <p className="text-sm text-muted-foreground mt-1">Flat rate</p>
                </div>
                <p className="text-xs text-muted-foreground mt-3 text-center">
                  Exotic pets over 1.9 kg use dog pricing below
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Dog Pricing Table */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="border-0 shadow-md">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-[hsl(var(--info-light))]">
                  <Dog className="h-6 w-6 text-[hsl(var(--info))]" />
                </div>
                <div>
                  <CardTitle className="text-xl" style={{ fontFamily: 'Fredoka, sans-serif' }}>Dog</CardTitle>
                  <CardDescription>Pricing by weight bracket</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {dogPricing.map((tier, i) => (
                  <motion.div
                    key={tier.range}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.25 + i * 0.04 }}
                    className="flex items-center justify-between px-6 py-4 hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="text-xs font-medium px-3 py-1 text-muted-foreground">
                        {tier.range}
                      </Badge>
                    </div>
                    <p className="text-lg font-bold text-primary" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      ${tier.price}
                    </p>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Info Note */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
          <div className="bg-primary/5 rounded-xl p-4 border border-primary/10">
            <p className="text-sm text-muted-foreground">
              {isNQCare ? (
                <><span className="font-semibold text-primary">Note:</span> Prices are automatically applied when submitting a new pet collection. The amount can be manually adjusted if needed during submission.</>
              ) : (
                <><span className="font-semibold text-primary">Note:</span> These are the standard service prices for Pure Souls aquacremation. Please share with pet owners as a reference.</>
              )}
            </p>
          </div>
        </motion.div>

        {/* Service Inclusions */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
          <Card className="border-0 shadow-md overflow-hidden">
            <div className="bg-gradient-to-br from-[hsl(185_45%_45%)] to-[hsl(185_50%_38%)] px-6 py-5">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Service Inclusions
              </h2>
              <p className="text-white/70 text-sm mt-1">What's included with every aquacremation</p>
            </div>
            <CardContent className="p-6">
              <div className="space-y-4">
                {[
                  'Vet collection & drop off',
                  'Guaranteed individual cremation',
                  'Complimentary Scattering Pouch & Paw Candle',
                  'Tree planting living tribute',
                  'Digital Certificates',
                ].map((item, i) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + i * 0.06 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-7 h-7 rounded-full bg-[hsl(185_40%_30%)] flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-white" />
                    </div>
                    <p className="text-sm font-medium">{item}</p>
                  </motion.div>
                ))}
              </div>

              {/* Male & Female pouches */}
              <div className="mt-6 pt-6 border-t border-border/50">
                <p className="text-xs text-muted-foreground text-center mb-4">Complimentary Scattering Pouch & Paw Candle</p>
                <div className="flex justify-center gap-8">
                  <div className="text-center">
                    <div className="w-32 h-32 rounded-2xl overflow-hidden mx-auto shadow-sm border border-[hsl(185_30%_80%)]">
                      <img src="/assets/pouch-male.png" alt="Male pouch - Blue" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm font-semibold mt-2 text-[hsl(185_40%_30%)]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Male</p>
                  </div>
                  <div className="text-center">
                    <div className="w-32 h-32 rounded-2xl overflow-hidden mx-auto shadow-sm border border-[hsl(15_40%_80%)]">
                      <img src="/assets/pouch-female.png" alt="Female pouch - Pink" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm font-semibold mt-2 text-[hsl(15_30%_45%)]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Female</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </Layout>
  );
};

export default PriceGuide;
